import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { AlertTriangle, CheckCircle, Loader2, Save } from 'lucide-react';
import { CenteredLoader } from '../CenteredLoader';
import { NoProjectSelectedState } from '../NoProjectSelectedState';
import { PageErrorBanner } from '../PageErrorBanner';
import { RuleSwitch } from '../red-flag-rules/RuleSwitch';
import { storeVisitTargetsAPI } from '../../services/api';
import { useAuthStore } from '../../stores/authStore';
import { splitTasksByStatus, type TaskListItem } from '../../utils/taskListFilter';
import type { RoleVisitTarget, ScheduledChange, VisitTargetsResponse } from './types';

/**
 * Master Data → Store Visits Configuration → Visit targets.
 *
 * One row per role of the project: whether the role visits stores, on how many days of a
 * week, and how many stores make a day count. One Save sends every changed role in one
 * request, from the day in "Valid from" beside it: the Monday of the current week unless
 * another day is picked. A past day is how earlier weeks get a target (the server then also
 * applies the save through today), a future day schedules the change. Under each role, in
 * grey: the day its current target applies from, and a change scheduled after today.
 *
 * A day before this week also carries back, unchanged and marked `carried`, every role left
 * alone that is on and had no target yet on that day (its first saved target starts after
 * it): "valid from 1 Sep" gives the weeks before a role's first target the target shown. A
 * role left alone that already had a target on that day is not sent, so an earlier target
 * is never overwritten by a save made for another role (the server skips such a role too,
 * should this page be out of date). Any other day carries only the roles edited, so a role
 * left alone, and a change already scheduled for it, stay as they are (and a page just
 * opened has nothing to save).
 *
 * The questionnaire list belongs to the page around this tab, which also shows its count
 * in the tab strip, so it is handed in rather than fetched twice.
 */

/** A store-visit questionnaire (a `tasks` row), as far as the counts here need it. */
export type VisitQuestionnaire = TaskListItem & { id: string };

/** Asked by the page before a tab switch leaves unsaved edits behind. */
export const DISCARD_TARGETS_PROMPT = 'Discard your unsaved changes to the visit targets?';

interface VisitTargetsTabProps {
  /** Where the page's tab row wants this tab's Save button. */
  actionSlot: HTMLElement | null;
  /** The project's store-visit questionnaires; null until they are known. */
  questionnaires: VisitQuestionnaire[] | null;
  /** Opens the Questionnaires tab on the questionnaires of one role. */
  onOpenQuestionnaires: (roleId: string) => void;
  /** Told whenever the table gains or loses unsaved edits, so leaving the tab can ask first. */
  onDirtyChange?: (dirty: boolean) => void;
}

type NumberField = 'days' | 'stores';

const LIMITS: Record<NumberField, { min: number; max: number }> = {
  days: { min: 1, max: 7 },
  stores: { min: 1, max: 50 },
};

interface DraftRow {
  roleId: string;
  roleName: string;
  phoneAccess: boolean;
  /** As saved: the day the current target applies from, the day of the role's first target,
   *  and the next scheduled change. */
  validFrom: string | null;
  firstValidFrom: string | null;
  nextChange: ScheduledChange | null;
  visitsExpected: boolean;
  // The two numbers as typed. Kept as text so an empty or half-typed box stays the way
  // the person left it until Save checks it; nothing is filled in for them.
  days: string;
  stores: string;
}

type RowErrors = Record<string, Partial<Record<NumberField, string>>>;

const LOAD_FAILED = 'Could not load the visit targets.';
const SAVE_FAILED = 'Could not save the visit targets.';
const PHONE_OFF_TITLE =
  'Store Visits is not switched on for this role on the phone. Switch it on in Settings › Access Management › Mobile Access.';

const apiMessage = (e: unknown, fallback: string): string =>
  (e as { response?: { data?: { message?: string } } })?.response?.data?.message || fallback;

const toDraft = (role: RoleVisitTarget): DraftRow => ({
  roleId: role.roleId,
  roleName: role.roleName,
  phoneAccess: role.phoneAccess,
  validFrom: role.validFrom ?? null,
  firstValidFrom: role.firstValidFrom ?? null,
  nextChange: role.nextChange ?? null,
  visitsExpected: role.visitsExpected,
  days: role.daysPerWeek != null ? String(role.daysPerWeek) : '',
  stores: role.storesPerDay != null ? String(role.storesPerDay) : '',
});

/** The typed text as a whole number, or null when it is not one. */
const wholeNumber = (text: string): number | null => (/^\d+$/.test(text.trim()) ? Number(text.trim()) : null);

const fieldError = (text: string, field: NumberField): string | null => {
  const { min, max } = LIMITS[field];
  const n = wholeNumber(text);
  return n !== null && n >= min && n <= max ? null : `Enter a whole number from ${min} to ${max}.`;
};

/** What Save would send for one role, as a string. The numbers of a switched-off role are not part of it. */
const rowPrint = (r: DraftRow): string =>
  r.visitsExpected ? `${r.roleId}:${wholeNumber(r.days) ?? r.days.trim()}:${wholeNumber(r.stores) ?? r.stores.trim()}` : `${r.roleId}:off`;

/** The same for the whole table: two tables with one fingerprint have nothing to save between them. */
const fingerprint = (rows: DraftRow[]): string => rows.map(rowPrint).join('|');

const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const ISO_DAY = /^(\d{4})-(\d{2})-(\d{2})$/;

/** A real calendar day written YYYY-MM-DD, as a UTC date; null for anything else. */
const utcDate = (isoDay: string | null | undefined): Date | null => {
  const m = ISO_DAY.exec(isoDay ?? '');
  if (!m) return null;
  const date = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])));
  return date.getUTCDate() === Number(m[3]) && date.getUTCMonth() === Number(m[2]) - 1 ? date : null;
};

/** '2026-10-12' → 'Mon 12 Oct'. Worked out in UTC, so the reader's own time zone cannot move the day. */
const dayLabel = (isoDay: string | undefined): string | null => {
  const date = utcDate(isoDay);
  return date ? `${DAY_NAMES[date.getUTCDay()]} ${date.getUTCDate()} ${MONTH_NAMES[date.getUTCMonth()]}` : null;
};

/** '2026-10-12' → '12 Oct', with the year when it is not this year's. */
const shortDay = (isoDay: string, thisYear: number | null): string => {
  const date = utcDate(isoDay);
  if (!date) return isoDay;
  const day = `${date.getUTCDate()} ${MONTH_NAMES[date.getUTCMonth()]}`;
  return thisYear === null || date.getUTCFullYear() === thisYear ? day : `${day} ${date.getUTCFullYear()}`;
};

const addDays = (isoDay: string, count: number): string | null => {
  const date = utcDate(isoDay);
  if (!date) return null;
  date.setUTCDate(date.getUTCDate() + count);
  return date.toISOString().slice(0, 10);
};

// The server's own bounds (resolveValidFrom): from 2020 to a year after today.
const EARLIEST_VALID_FROM = '2020-01-01';
const VALID_FROM_TITLE = 'The day these targets start. A day before this week also gives roles with no target yet then the one shown, so past weeks are judged too.';
const NO_VALID_FROM = 'Pick the day the targets apply from.';

const plural = (n: number, word: string): string => `${n} ${word}${n === 1 ? '' : 's'}`;

/** A scheduled change in a few words: "From 26 Oct: 4 days, 2 stores" or "Off from 26 Oct". */
const changeLine = (change: ScheduledChange, thisYear: number | null): string => {
  const from = shortDay(change.validFrom, thisYear);
  if (!change.visitsExpected || change.daysPerWeek == null || change.storesPerDay == null) return `Off from ${from}`;
  return `From ${from}: ${plural(change.daysPerWeek, 'day')}, ${plural(change.storesPerDay, 'store')}`;
};

export default function VisitTargetsTab({ actionSlot, questionnaires, onOpenQuestionnaires, onDirtyChange }: VisitTargetsTabProps) {
  const selectedProjectId = useAuthStore((state) => state.selectedProjectId);

  // The project whose targets are on screen. It trails the selected project for one render
  // when the project is switched, so an answer on its way for the old one can be told apart.
  const [projectId, setProjectId] = useState(selectedProjectId);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  // The saved table (null until it has loaded) and the table as edited.
  const [baseline, setBaseline] = useState<DraftRow[] | null>(null);
  const [draft, setDraft] = useState<DraftRow[]>([]);
  // The day a save applies from, as picked, and the Monday it starts at. Today (India) is
  // the server's, for the year shown in dates and the latest day that may be picked.
  const [validFrom, setValidFrom] = useState('');
  const [weekStart, setWeekStart] = useState<string | null>(null);
  const [today, setToday] = useState<string | null>(null);
  const [errors, setErrors] = useState<RowErrors>({});
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  // Counts the Saves that were refused, so the first refused box can be brought into view:
  // Save sits above the table, and in a long one the box may be well below the fold.
  const [refusals, setRefusals] = useState(0);
  const tableRef = useRef<HTMLDivElement>(null);

  // What each role was saved as, to tell an edited row from one left alone.
  const savedPrints = useMemo(() => new Map((baseline ?? []).map((row) => [row.roleId, rowPrint(row)])), [baseline]);
  // A row left alone goes with a save dated before this week only when it is on and had no
  // target yet on that day (its first target starts after it): the target shown then covers
  // the weeks before. One that already had a target that day (on or off) is left as it is.
  const extendsBack = (row: DraftRow): boolean =>
    ISO_DAY.test(validFrom) && weekStart !== null && validFrom < weekStart
    && row.visitsExpected && row.firstValidFrom !== null && validFrom < row.firstValidFrom;
  const dirty = baseline !== null && (fingerprint(draft) !== fingerprint(baseline) || draft.some(extendsBack));
  const shownProject = useRef(projectId);
  shownProject.current = projectId;

  // A project switch happens in the top bar, outside this page, and cannot be held back
  // from here. The table follows it, and edits not yet saved for the project left behind
  // are dropped, as on every other page. (Setting the project back from here was tried and
  // left the header showing the other project's logo.)
  useEffect(() => {
    setProjectId(selectedProjectId);
  }, [selectedProjectId]);

  useEffect(() => {
    setBaseline(null);
    setDraft([]);
    setValidFrom('');
    setWeekStart(null);
    setToday(null);
    setErrors({});
    setLoadError(null);
    setSaveError(null);
    setNotice(null);
    if (!projectId) {
      setLoading(false);
      return undefined;
    }
    let alive = true;
    setLoading(true);
    storeVisitTargetsAPI
      .get(projectId)
      .then((res) => {
        if (!alive) return;
        const data = res.data?.data as VisitTargetsResponse | undefined;
        // An answer without a role list is a failed load, not "this project has no roles".
        if (!data || !Array.isArray(data.roles)) {
          setLoadError(LOAD_FAILED);
          return;
        }
        const rows = data.roles.map(toDraft);
        setBaseline(rows);
        setDraft(rows);
        setWeekStart(data.weekStart ?? null);
        setValidFrom(data.weekStart ?? '');
        setToday(data.today ?? null);
      })
      .catch((e) => alive && setLoadError(apiMessage(e, LOAD_FAILED)))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [projectId]);

  // Leaving the page (reload / close tab) with unsaved edits asks first.
  useEffect(() => {
    if (!dirty) return undefined;
    const onBeforeUnload = (e: BeforeUnloadEvent) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [dirty]);

  useEffect(() => {
    onDirtyChange?.(dirty);
    return () => onDirtyChange?.(false);
  }, [dirty, onDirtyChange]);

  useEffect(() => {
    if (refusals > 0) tableRef.current?.querySelector<HTMLInputElement>('input[aria-invalid="true"]')?.focus();
  }, [refusals]);

  useEffect(() => {
    if (!notice) return undefined;
    const timer = window.setTimeout(() => setNotice(null), 4000);
    return () => window.clearTimeout(timer);
  }, [notice]);

  // How many active questionnaires a role is asked: those for every role plus those naming
  // it. Counted by the rule the Questionnaires tab filters by, so the number in a row is
  // the number that tab lists when the row's link is followed.
  const activeCountFor = useMemo(() => {
    if (!questionnaires) return null;
    return (roleId: string) => splitTasksByStatus(questionnaires, { search: '', roleId }).active.length;
  }, [questionnaires]);

  const editRow = (roleId: string, change: Partial<DraftRow>) => {
    setNotice(null);
    setDraft((rows) => rows.map((r) => (r.roleId === roleId ? { ...r, ...change } : r)));
  };

  const toggleVisits = (roleId: string, next: boolean) => {
    editRow(roleId, { visitsExpected: next });
    // A refusal belongs to the boxes that were on screen; switched off, they are gone.
    setErrors((prev) => (prev[roleId] ? { ...prev, [roleId]: {} } : prev));
  };

  const editNumber = (roleId: string, field: NumberField, text: string) => {
    editRow(roleId, field === 'days' ? { days: text } : { stores: text });
    // The refusal was about what the box held; it is checked again on the next Save.
    setErrors((prev) => (prev[roleId]?.[field] ? { ...prev, [roleId]: { ...prev[roleId], [field]: undefined } } : prev));
  };

  const save = async () => {
    if (!projectId || !dirty || saving) return;
    if (!utcDate(validFrom)) {
      setSaveError(NO_VALID_FROM);
      return;
    }
    const found: RowErrors = {};
    for (const row of draft) {
      if (!row.visitsExpected) continue;
      const days = fieldError(row.days, 'days');
      const stores = fieldError(row.stores, 'stores');
      if (days || stores) found[row.roleId] = { ...(days ? { days } : {}), ...(stores ? { stores } : {}) };
    }
    setErrors(found);
    if (Object.keys(found).length > 0) {
      setRefusals((n) => n + 1);
      return;
    }

    setSaving(true);
    setSaveError(null);
    setNotice(null);
    try {
      // The roles this person changed, and the ones an earlier week carries back (marked
      // carried, so the server only fills days they had no target and never overwrites one).
      // Any other row left alone may be out of date (someone else saved it meanwhile), and
      // sending it would undo their change.
      const res = await storeVisitTargetsAPI.save(
        projectId,
        draft.flatMap((row) => {
          const edited = rowPrint(row) !== savedPrints.get(row.roleId);
          if (!edited && !extendsBack(row)) return [];
          return [{
            roleId: row.roleId,
            visitsExpected: row.visitsExpected,
            daysPerWeek: row.visitsExpected ? wholeNumber(row.days) : null,
            storesPerDay: row.visitsExpected ? wholeNumber(row.stores) : null,
            ...(edited ? {} : { carried: true }),
          }];
        }),
        validFrom,
      );
      // The project was switched while the save was on its way: the answer is not this table's.
      if (shownProject.current !== projectId) return;
      const data = res.data?.data as VisitTargetsResponse | undefined;
      const rows = data && Array.isArray(data.roles) ? data.roles.map(toDraft) : draft;
      setBaseline(rows);
      setDraft(rows);
      // The next save starts again from this week's Monday.
      const monday = data?.weekStart ?? weekStart;
      setWeekStart(monday);
      setValidFrom(monday ?? '');
      if (data?.today) setToday(data.today);
      // A server from before valid-from dates applied the save from this week's Monday.
      const from = dayLabel(data?.savedFrom ?? data?.weekStart);
      setNotice(from ? `Saved. Applies from ${from}.` : 'Saved.');
    } catch (e) {
      if (shownProject.current === projectId) setSaveError(apiMessage(e, SAVE_FAILED));
    } finally {
      setSaving(false);
    }
  };

  if (!selectedProjectId) {
    return <NoProjectSelectedState message="Select a project from the dropdown in the menu bar." />;
  }
  if (loading) return <CenteredLoader />;
  if (loadError || baseline === null) return <PageErrorBanner message={loadError ?? LOAD_FAILED} />;

  const numberCell = (row: DraftRow, field: NumberField, label: string) => {
    if (!row.visitsExpected) return <span className="text-gray-400">—</span>;
    const error = errors[row.roleId]?.[field];
    const errorId = `visit-target-${row.roleId}-${field}-error`;
    return (
      <div>
        <input
          type="number"
          min={LIMITS[field].min}
          max={LIMITS[field].max}
          step={1}
          inputMode="numeric"
          aria-label={`${label} for ${row.roleName}`}
          aria-invalid={Boolean(error)}
          aria-describedby={error ? errorId : undefined}
          value={row[field]}
          disabled={saving}
          onChange={(e) => editNumber(row.roleId, field, e.target.value)}
          className={`w-20 rounded-lg border px-2 py-1 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 disabled:opacity-50 ${
            error ? 'border-red-400' : 'border-gray-300'
          }`}
        />
        {error && <p id={errorId} className="mt-1 text-xs text-red-700">{error}</p>}
      </div>
    );
  };

  const thisYear = utcDate(today ?? weekStart)?.getUTCFullYear() ?? null;
  const latestValidFrom = today ? addDays(today, 366) : null;

  /** Grey, under the figures: since when today's target applies, and the next change. */
  const scheduleLines = (row: DraftRow) => {
    const edited = rowPrint(row) !== savedPrints.get(row.roleId);
    const since = !edited && row.visitsExpected && row.validFrom ? `From ${shortDay(row.validFrom, thisYear)}` : null;
    const next = row.nextChange ? changeLine(row.nextChange, thisYear) : null;
    if (!since && !next) return null;
    return (
      <div className="mt-1 space-y-0.5 text-xs text-gray-500">
        {since && <p>{since}</p>}
        {next && <p>{next}</p>}
      </div>
    );
  };

  const questionnairesCell = (row: DraftRow) => {
    if (!row.visitsExpected || !activeCountFor) return <span className="text-gray-400">—</span>;
    const n = activeCountFor(row.roleId);
    if (n === 0) return <span className="text-gray-400">None</span>;
    return (
      <button
        type="button"
        onClick={() => onOpenQuestionnaires(row.roleId)}
        aria-label={`${n.toLocaleString()} active ${n === 1 ? 'questionnaire' : 'questionnaires'} for ${row.roleName}`}
        className="font-medium text-primary-600 hover:text-primary-700 hover:underline"
      >
        {n.toLocaleString()} active
      </button>
    );
  };

  return (
    <div className="space-y-6">
      {actionSlot && createPortal(
        <>
          <label className="flex items-center gap-2 text-sm font-medium text-gray-600" title={VALID_FROM_TITLE}>
            Valid from
            <input
              type="date"
              aria-label="Valid from"
              value={validFrom}
              min={EARLIEST_VALID_FROM}
              max={latestValidFrom ?? undefined}
              disabled={saving}
              onChange={(e) => {
                setValidFrom(e.target.value);
                setNotice(null);
                setSaveError((prev) => (prev === NO_VALID_FROM ? null : prev));
              }}
              className="rounded-lg border border-gray-300 px-2 py-1.5 text-sm text-gray-900 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 disabled:opacity-50"
            />
          </label>
          <button
            type="button"
            onClick={() => void save()}
            disabled={saving || !dirty}
            className="inline-flex items-center gap-1.5 rounded-lg bg-primary-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary-700 disabled:opacity-50"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> : <Save className="h-4 w-4" aria-hidden />}
            Save
          </button>
        </>,
        actionSlot,
      )}

      {notice && (
        <div role="status" className="flex items-center gap-2 rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-700">
          <CheckCircle className="h-4 w-4 flex-shrink-0" aria-hidden />
          {notice}
        </div>
      )}
      {saveError && <PageErrorBanner message={saveError} />}

      <div ref={tableRef} className="overflow-hidden rounded-lg border border-gray-100 bg-white">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50/70 text-left text-xs font-semibold text-gray-500">
              <tr>
                <th className="whitespace-nowrap px-6 py-4">Role</th>
                <th className="whitespace-nowrap px-6 py-4">Store visits</th>
                <th className="whitespace-nowrap px-6 py-4">Days a week</th>
                <th className="whitespace-nowrap px-6 py-4">Stores a day</th>
                <th className="whitespace-nowrap px-6 py-4">Questionnaires</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 bg-white">
              {draft.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-6 py-12 text-center text-sm text-gray-500">
                    This project has no roles yet.
                  </td>
                </tr>
              ) : (
                draft.map((row) => (
                  <tr key={row.roleId} className="hover:bg-gray-50">
                    <td className="whitespace-nowrap px-6 py-4 text-gray-900">{row.roleName}</td>
                    <td className="whitespace-nowrap px-6 py-4">
                      <div className="flex items-center gap-2">
                        <RuleSwitch
                          checked={row.visitsExpected}
                          onChange={(next) => toggleVisits(row.roleId, next)}
                          label={`Store visits for ${row.roleName}`}
                          disabled={saving}
                        />
                        {row.visitsExpected && !row.phoneAccess && (
                          <span role="img" aria-label={PHONE_OFF_TITLE} title={PHONE_OFF_TITLE}>
                            <AlertTriangle className="h-4 w-4 text-amber-500" aria-hidden />
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="whitespace-nowrap px-6 py-4">
                      {numberCell(row, 'days', 'Days a week')}
                      {scheduleLines(row)}
                    </td>
                    <td className="whitespace-nowrap px-6 py-4">{numberCell(row, 'stores', 'Stores a day')}</td>
                    <td className="whitespace-nowrap px-6 py-4">{questionnairesCell(row)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
